// γ-3-G — Role-based, field-level edit scope for Freight Deals.
//
// Single source of truth: same detail page is shown to every actor, but the
// editor buttons + popovers gate which fields are writable. Backed by the
// per-deal assignment (arazo_user_id = pricer, ugyfelszolgalat_user_id /
// felelos_user_id / deal_tulajdonos_user_id / created_by = handler).
//
// Extensible — the `flags` map can grow to drive the (future) customer-portal
// 3-tier visibility (customer / forwarder / internal).
//
// Returns:
//   {
//     scope:       'admin' | 'pricer' | 'handler' | 'both' | 'viewer',
//     buttonLabel: 'Szerkesztés' | 'Árazás' | null,
//     flags: {
//       core, route, cargo, customer, stops, assignment, dates,
//       identification, tender, documents, pricing, surcharges,
//       validity, kpi, notes, execution
//     },
//   }
//
// Each flag is a boolean — `true` means the actor can write that field group.
// When the deal is unknown (e.g. creating new), the actor is treated as the
// registrant ⇒ full handler scope; admins always get full scope.

export const FIELD_GROUPS_PRICING = new Set([
  // PARAMS.k values that count as "pricing" — drives popover gating in the
  // side panel. Anything not in this set falls under handler scope.
  "celar", "ajanlati_margin_pct", "elfogadott_dij",
  "fuvarozas_dij", "fuvarozas_koltseg", "fuvarozas_margin",
  "szallitmanyozas_dij", "szallitmanyozas_koltseg", "szallitmanyozas_margin",
  "added_value_dij", "added_value_koltseg", "added_value_margin",
  "konkurencia_dij", "amount",
  "ajanlat_ervenyessege",                 // offer validity — pricing-side
]);

export const FIELD_GROUPS_EXECUTION = new Set([
  // γ-3-G — Strict carrier-fault delay tracking. Backend rejects writes when
  // deal_stage != 'executed'; the frontend mirrors that gate so the field
  // greys out unless the deal is in `executed`.
  "delay_minutes", "delay_notes", "executed_notes",
  // G.1 — "Who & What executes this freight" — same executed-only rule.
  "vehicle_provider", "subcontractor_name",
  "vehicle_plate", "trailer_plate", "driver_name",
]);

const FULL_FLAGS = {
  core: true, route: true, cargo: true, customer: true, stops: true,
  assignment: true, dates: true, identification: true, tender: true,
  documents: true, pricing: true, surcharges: true, validity: true,
  kpi: true, notes: true, execution: true,
};

const PRICER_FLAGS = {
  core: false, route: false, cargo: false, customer: false, stops: false,
  assignment: false, dates: false, identification: false, tender: false,
  documents: true, pricing: true, surcharges: true, validity: true,
  kpi: false, notes: true, execution: false,
};

const HANDLER_FLAGS = {
  core: true, route: true, cargo: true, customer: true, stops: true,
  assignment: true, dates: true, identification: true, tender: true,
  documents: true, pricing: false, surcharges: false, validity: false,
  kpi: true, notes: true, execution: true,
};

const VIEWER_FLAGS = Object.fromEntries(Object.keys(FULL_FLAGS).map((k) => [k, false]));

/**
 * Derive the actor's edit scope for the given deal.
 *
 * @param {object|null} user - { id, role, ... }
 * @param {object|null} deal - the loaded LR doc (or null for new-deal mode)
 * @returns {object}
 */
export function deriveEditScope(user, deal) {
  if (!user) return { scope: "viewer", buttonLabel: null, flags: { ...VIEWER_FLAGS } };

  const isAdmin = user.role === "platform_admin" || user.role === "tenant_admin";
  if (isAdmin) {
    return { scope: "admin", buttonLabel: "Szerkesztés", flags: { ...FULL_FLAGS } };
  }

  // New-deal mode (no deal yet) — the actor is the registrant, so full handler
  // scope. They can still leave pricing fields blank for a pricer to fill.
  if (!deal) {
    return { scope: "handler", buttonLabel: "Szerkesztés", flags: { ...HANDLER_FLAGS, pricing: true, surcharges: true, validity: true } };
  }

  const uid = user.id;
  const isPricer = !!uid && uid === deal.arazo_user_id;
  const isHandler =
    !!uid &&
    (uid === deal.ugyfelszolgalat_user_id ||
      uid === deal.felelos_user_id ||
      uid === deal.deal_tulajdonos_user_id ||
      uid === deal.created_by);

  if (isPricer && isHandler) {
    return { scope: "both", buttonLabel: "Szerkesztés", flags: { ...FULL_FLAGS } };
  }
  if (isPricer) {
    return { scope: "pricer", buttonLabel: "Árazás", flags: { ...PRICER_FLAGS } };
  }
  if (isHandler) {
    return { scope: "handler", buttonLabel: "Szerkesztés", flags: { ...HANDLER_FLAGS } };
  }
  // Not directly assigned but inside the tenant — default to handler scope
  // (so colleagues can still help). Pricing stays locked unless explicitly
  // assigned as the árazó.
  return { scope: "handler", buttonLabel: "Szerkesztés", flags: { ...HANDLER_FLAGS } };
}

/**
 * Can the actor write to a specific PARAMS-catalogue field key?
 * Used by the side-panel popover to grey out locked fields per scope + stage.
 */
export function canEditParamKey(scope, dealStage, paramKey) {
  if (!scope) return false;
  if (scope.scope === "admin") {
    // Even admins are bound by the executed-only stage gate.
    if (FIELD_GROUPS_EXECUTION.has(paramKey)) return dealStage === "executed";
    return true;
  }
  if (FIELD_GROUPS_EXECUTION.has(paramKey)) {
    return dealStage === "executed" && (scope.flags.execution || false);
  }
  if (FIELD_GROUPS_PRICING.has(paramKey)) {
    return !!scope.flags.pricing;
  }
  // Otherwise the field falls under handler scope.
  return !!scope.flags.identification || !!scope.flags.core || !!scope.flags.cargo || !!scope.flags.notes || !!scope.flags.assignment || !!scope.flags.dates;
}
